import { Loader2 } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { ErrorService } from "@/lib/errors";
import { usePostHog } from "@/hooks/usePostHog";
import { syncCrispSupportSession } from "@/hooks/useCrisp";
import { isPublicAuthPath } from "@/lib/publicAuthPaths";
import { useQueryClient } from "@tanstack/react-query";
import { useConversationCasesStore } from '@/features/hector-agent-casediscovery-v3/stores/conversationCasesStore';
import { useActiveSessionsStore } from "@/features/hector-agent-casediscovery-v3/stores/activeSessionsStore";
import { oublierLesFichiersRendusAuComposeur } from "@/features/hector-agent-casediscovery-v3/state/fichiersRendusAuComposeur";
import { evictCanvasForUserChange } from "@/features/redaction/state/canvasEviction";


interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Refs lues dans l'effet d'auth « monté une fois » : permettent à la callback
  // du listener de voir le navigate et l'user courants SANS re-souscrire à chaque
  // navigation (cf. effet ci-dessous, deps []).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const userRef = useRef<User | null>(user);
  userRef.current = user;
  const { identifyUser, trackAuth } = usePostHog();
  const queryClient = useQueryClient();




  // Function to refresh the session data
  const refreshSession = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    } catch {
      // Échec de rafraîchissement de session silencieux : apiClient gère les 401.
    }
  }, []);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        setUser(session?.user ?? null);
        
        // Only redirect if not already on auth page and not authenticated
        // Allow access to /complete-profile for invite flow
        if (!session?.user && !isPublicAuthPath(window.location.pathname)) {
          navigateRef.current("/auth");
        }
      } catch (error) {
        // Session recovery errors are silently handled — apiClient manages 401 redirects
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const previousUser = userRef.current;
      setUser(session?.user ?? null);
      setIsLoading(false);
      
      // Selectively clear user-specific queries when user changes to prevent stale data
      if (previousUser?.id !== session?.user?.id) {
        // Only clear user-specific queries instead of entire cache. The global
        // conversation chrome is mounted across auth transitions; if we leave
        // `['conversations']` or v3 history in cache, account B can briefly see
        // account A's sidebar row and then open `/assistant?c=...`, which the
        // backend correctly rejects as 404 (incident resource-sharing 2026-07-07).
        queryClient.removeQueries({ queryKey: ['subscription'] });
        queryClient.removeQueries({ queryKey: ['cabinet-subscription'] });
        queryClient.removeQueries({ queryKey: ['license-usage'] });
        queryClient.removeQueries({ queryKey: ['is-cabinet-admin'] });
        queryClient.removeQueries({ queryKey: ['cabinet-members'] });
        queryClient.removeQueries({ queryKey: ['cases'] });
        queryClient.removeQueries({ queryKey: ['documents'] });
        queryClient.removeQueries({ queryKey: ['conversations'] });
        queryClient.removeQueries({ queryKey: ['messages'] });
        queryClient.removeQueries({ queryKey: ['v3-conversation-history'] });
        // Personal skill prompts are user-private. User-scoped keys prevent
        // reuse; this purge also removes the previous account from memory.
        queryClient.removeQueries({ queryKey: ['agent-skills'] });
        useActiveSessionsStore.getState().clear();
        useConversationCasesStore.getState().vider();
        // Fichiers qu'un message de l'avocat A n'a pas pu emporter (#2066) : ils
        // attendent le composeur de leur conversation hors de l'arbre React, et
        // B les retrouverait en ouvrant un fil que A lui a partagé.
        oublierLesFichiersRendusAuComposeur();
        // Le canvas de rédaction vit désormais dans la coque (#856), donc il
        // survit à cette transition — l'avocat B y trouverait le document de
        // l'avocat A, pas une ligne de liste mais un acte entier. Cette
        // éviction abandonne d'abord ce qui attendait d'être enregistré (ce
        // brouillon appartient à A) puis démonte réellement l'éditeur.
        evictCanvasForUserChange();

        if (
          previousUser?.id &&
          session?.user?.id &&
          window.location.pathname === "/assistant" &&
          window.location.search.includes("c=")
        ) {
          navigateRef.current("/assistant", { replace: true });
        }
      }
      
      // Handle PostHog user identification and tracking
      try {
        if (session?.user) {
          // User logged in - identify and track
          identifyUser(session.user.id, {
            email: session.user.email,
            user_id: session.user.id,
            signed_up_at: session.user.created_at,
            last_sign_in_at: session.user.last_sign_in_at,
          });
          
          if (event === 'SIGNED_IN') {
            trackAuth('login', session.user.email);
          }
        } else if (event === 'SIGNED_OUT') {
          // User logged out
          trackAuth('logout');
        }
      } catch {
        // Échec de tracking analytics silencieux : ne doit jamais bloquer l'auth.
      }

      // Only redirect if not already on auth page and not authenticated
      // Allow access to /complete-profile for invite flow
      if (!session?.user && !isPublicAuthPath(window.location.pathname)) {
        navigateRef.current("/auth");
      }
    });

    return () => subscription.unsubscribe();
    // WHY [] (monté une fois) : ce bootstrap initialise getSession + le listener
    // onAuthStateChange, qui doivent vivre toute la durée de l'app. L'ancienne
    // dépendance `location.pathname` ré-exécutait `getInitialSession()` à CHAQUE
    // navigation → `setIsLoading(true)` → le gate `if (isLoading) return <spinner>`
    // démontait puis remontait TOUT l'arbre applicatif à chaque clic (cause racine
    // du flickering / « rechargement brutal », #238). navigate et l'user courant
    // sont lus via refs ; le pathname pour la redirection via window.location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Crisp (chat de support) : UN seul point de réconciliation, rejoué quand la
  // session ou la page change. La règle vit dans `syncCrispSupportSession` —
  // pas de support ni d'identité poussée sur les pages publiques (inscription,
  // connexion, complétion de profil), session Crisp réinitialisée dès que plus
  // personne n'est connecté.
  // WHY attendre `!isLoading` : pendant le bootstrap `user` vaut `null` pour
  // tout le monde ; réconcilier à ce moment réinitialiserait la conversation
  // support d'un avocat connecté à chaque rechargement.
  // WHY des primitives en dépendances : Supabase recrée l'objet `user` à chaque
  // rafraîchissement de jeton, ce qui n'a pas à repousser de commandes à Crisp.
  const crispUserId = user?.id ?? null;
  const crispUserEmail = user?.email ?? null;
  const crispUserFullName: unknown = user?.user_metadata?.full_name;
  useEffect(() => {
    if (isLoading) return;
    syncCrispSupportSession({
      user: crispUserId === null
        ? null
        : {
            id: crispUserId,
            email: crispUserEmail,
            fullName: typeof crispUserFullName === "string" ? crispUserFullName : null,
          },
      pathname: location.pathname,
    });
  }, [isLoading, crispUserId, crispUserEmail, crispUserFullName, location.pathname]);

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      await supabase.auth.signOut();
      navigate("/auth");
    } catch (error) {
      ErrorService.handle(error instanceof Error ? error : new Error("Erreur lors de la déconnexion"), { userMessage: "L'enregistrement n'a pas abouti. Veuillez réessayer.", context: { action: 'auth_signout' } });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Value mémoïsée : AuthProvider re-render à chaque navigation (useLocation pour
  // le gate + l'effet Crisp), or un objet littéral recréé ferait re-render TOUS
  // les consumers useAuth() (DashboardLayout, pages…) à chaque nav. (#238)
  // Déclarée AVANT le early-return ci-dessous (rules-of-hooks).
  const value = useMemo(
    () => ({ user, isLoading, signOut, refreshSession }),
    [user, isLoading, signOut, refreshSession],
  );

  // Provide loading state — n'apparaît qu'au bootstrap initial (isLoading ne
  // bascule plus à chaque navigation depuis le fix #238 ci-dessus).
  if (isLoading && !isPublicAuthPath(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-grey-100">
        <Loader2 className="w-4 h-4 animate-spin text-charcoal" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
